import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  listAdminUsersApi,
  getAdminUserApi,
  updateAdminUserPointApi,
  deleteAdminUserApi,
  restoreAdminUserApi,
} from "api/admin";
import {
  C,
  Panel,
  Row,
  SkeletonRows,
  Empty,
  UserTypeChip,
  InfoRow,
  DetailFrame,
  SearchBar,
  FilterPills,
  Pagination,
  formatDate,
  formatPhone,
  relativeTime,
  primaryBtn,
  ghostBtn,
  ghostBtnRed,
  dangerBtnSm,
  primaryBtnSm,
  inputStyle,
  CsvExportButton,
} from "./admin_ui";

export function AdminUsersList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const userType = params.get("type") || "";
  const search = params.get("q") || "";
  const includeDeleted = params.get("deleted") === "1";
  const page = parseInt(params.get("page") || "1", 10) || 1;

  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminUsersApi({
        userType: userType || undefined,
        search: search || undefined,
        includeDeleted: includeDeleted ? "1" : undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success) setData({ items: r.items || [], total: r.total || 0 });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userType, search, includeDeleted, page]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            회원 관리
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            전체 {data.total.toLocaleString()}명
          </div>
        </div>
        <CsvExportButton kind="users" />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <FilterPills
          value={userType}
          onChange={(v) => setParam("type", v)}
          options={[
            { label: "전체", value: "" },
            { label: "농가", value: "농가" },
            { label: "수리기사", value: "수리기사" },
          ]}
        />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: C.body,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setParam("deleted", e.target.checked ? "1" : "")}
          />
          탈퇴 회원 포함
        </label>
        <div style={{ flex: 1 }} />
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => setParam("q", searchInput.trim())}
          placeholder="이름 / 아이디 / 전화번호"
        />
      </div>

      <Panel title="회원 목록" count={data.total}>
        {loading ? (
          <SkeletonRows count={8} />
        ) : error ? (
          <Empty label={error} />
        ) : data.items.length === 0 ? (
          <Empty />
        ) : (
          data.items.map((u) => (
            <Row
              key={u.docId}
              onClick={() => navigate(`/miadmin/users/${u.docId}`)}
              left={
                <span>
                  <span
                    style={{
                      color: u.deleteDate ? C.sub : C.ink,
                      textDecoration: u.deleteDate ? "line-through" : "none",
                    }}
                  >
                    {u.name || u.id}
                  </span>
                  <span style={{ marginLeft: 8 }}>
                    <UserTypeChip userType={u.userType} />
                  </span>
                  {u.deleteDate && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 6,
                        background: C.red50,
                        color: C.red700,
                      }}
                    >
                      탈퇴
                    </span>
                  )}
                </span>
              }
              mid={
                <>
                  {u.id} · {formatPhone(u.phone) || "—"}
                </>
              }
              right={relativeTime(u.createDate)}
            />
          ))
        )}
        <Pagination
          page={page}
          limit={20}
          total={data.total}
          onChange={(p) => setParam("page", String(p))}
        />
      </Panel>
    </>
  );
}

export function AdminUserDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingPoint, setEditingPoint] = useState(false);
  const [pointInput, setPointInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  const reload = async () => {
    setLoading(true);
    setError("");
    const r = await getAdminUserApi(docId);
    if (r?.success) setData(r.data);
    else setError(r?.error || "조회 실패");
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await reload();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const u = data || {};

  const savePoint = async () => {
    const n = parseInt(pointInput, 10);
    if (Number.isNaN(n) || n < 0) {
      alert("0 이상의 정수만 입력 가능합니다.");
      return;
    }
    setBusy(true);
    setDone("");
    const r = await updateAdminUserPointApi(docId, n);
    setBusy(false);
    if (r?.success) {
      setEditingPoint(false);
      setDone("포인트가 수정되었습니다.");
      await reload();
    } else {
      alert(r?.error || "수정 실패");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`${u.name || u.id} 회원을 탈퇴 처리하시겠습니까?`)) return;
    setBusy(true);
    const r = await deleteAdminUserApi(docId);
    setBusy(false);
    if (r?.success) {
      setDone("탈퇴 처리되었습니다.");
      await reload();
    } else {
      alert(r?.error || "처리 실패");
    }
  };

  const handleRestore = async () => {
    setBusy(true);
    const r = await restoreAdminUserApi(docId);
    setBusy(false);
    if (r?.success) {
      setDone("복구되었습니다.");
      await reload();
    } else {
      alert(r?.error || "처리 실패");
    }
  };

  return (
    <DetailFrame
      title="회원 상세"
      backTo="/miadmin/users"
      onBack={() => navigate(-1)}
      badge={u.userType ? <UserTypeChip userType={u.userType} /> : null}
      actions={
        !loading && !error ? (
          u.deleteDate ? (
            <button
              type="button"
              onClick={handleRestore}
              disabled={busy}
              style={primaryBtnSm}
            >
              복구
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              style={dangerBtnSm}
            >
              탈퇴 처리
            </button>
          )
        ) : null
      }
    >
      {loading ? (
        <SkeletonRows count={6} />
      ) : error ? (
        <Empty label={error} />
      ) : (
        <>
          {done && (
            <div
              style={{
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: C.greenBg,
                border: `1px solid ${C.greenBorder}`,
                color: C.greenDark,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {done}
            </div>
          )}
          <InfoRow label="이름" value={u.name} />
          <InfoRow label="아이디" value={u.id} mono />
          <InfoRow label="docId" value={u.docId} mono />
          <InfoRow label="회원 유형" value={u.userType} />
          <InfoRow label="휴대폰" value={formatPhone(u.phone)} />
          <InfoRow
            label="주소"
            value={
              [u.address, u.addressDetail].filter(Boolean).join(" ") || null
            }
          />
          <InfoRow
            label={u.userType === "수리기사" ? "전문 분야" : "주요 작물/축산"}
            value={u.material}
          />
          <InfoRow
            label="포인트"
            value={
              editingPoint ? (
                <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="number"
                    value={pointInput}
                    onChange={(e) => setPointInput(e.target.value)}
                    style={{ ...inputStyle, height: 36, width: 140 }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={savePoint}
                    disabled={busy}
                    style={primaryBtnSm}
                  >
                    저장
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingPoint(false)}
                    disabled={busy}
                    style={ghostBtn}
                  >
                    취소
                  </button>
                </span>
              ) : (
                <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontWeight: 800, color: C.greenDark }}>
                    {(Number(u.point) || 0).toLocaleString()} P
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPointInput(String(Number(u.point) || 0));
                      setEditingPoint(true);
                    }}
                    style={{ ...ghostBtn, padding: "4px 10px", fontSize: 11 }}
                  >
                    수정
                  </button>
                </span>
              )
            }
          />
          <InfoRow label="평점" value={u.rating} />
          <InfoRow label="가입일" value={formatDate(u.createDate)} />
          <InfoRow label="마지막 로그인" value={formatDate(u.lastLogin)} />
          {u.deleteDate && (
            <InfoRow
              label="탈퇴일"
              value={
                <span style={{ color: C.red, fontWeight: 700 }}>
                  {formatDate(u.deleteDate)}
                </span>
              }
            />
          )}
        </>
      )}
    </DetailFrame>
  );
}
